import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const ReproductionContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useReproduction = () => {
  const context = useContext(ReproductionContext);
  if (!context) {
    throw new Error('useReproduction must be used within a ReproductionProvider');
  }
  return context;
};

export const ReproductionProvider = ({ children }) => {
  const [pregnancies, setPregnancies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPregnancies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/pregnancies`, { headers });
      setPregnancies(response.data);
    } catch (error) {
      console.error('Error al cargar embarazos:', error);
      toast.error('Error al cargar los embarazos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPregnancies();
  }, [fetchPregnancies]);

  const addPregnancy = async (pregnancyData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/pregnancies`, pregnancyData, { headers });
      setPregnancies(prev => [...prev, response.data]);
      toast.success('Embarazo registrado exitosamente');
      return response.data;
    } catch (error) {
      console.error('Error al registrar embarazo:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar embarazo');
      throw error;
    }
  };

  const addCheckup = async (pregnancyId, checkupData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/pregnancies/${pregnancyId}/checkup`, checkupData, { headers });
      
      // Refresh pregnancies
      await fetchPregnancies();
      toast.success('Revisión agregada exitosamente');
    } catch (error) {
      console.error('Error al agregar revisión:', error);
      toast.error('Error al agregar revisión');
      throw error;
    }
  };

  const addPalpation = async (pregnancyId, palpationData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/pregnancies/${pregnancyId}/palpation`, palpationData, { headers });
      
      // Refresh pregnancies
      await fetchPregnancies();
      toast.success('Palpación registrada exitosamente');
    } catch (error) {
      console.error('Error al registrar palpación:', error);
      toast.error('Error al registrar palpación');
      throw error;
    }
  };

  const completePregnancy = async (pregnancyId, birthData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/pregnancies/${pregnancyId}/complete`, birthData, { headers });
      
      // Refresh pregnancies
      await fetchPregnancies();
      toast.success('Parto registrado exitosamente');
    } catch (error) {
      console.error('Error al completar embarazo:', error);
      toast.error('Error al registrar parto');
      throw error;
    }
  };

  const abortPregnancy = async (pregnancyId, abortionData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/pregnancies/${pregnancyId}/abort`, abortionData, { headers });
      
      // Refresh pregnancies
      await fetchPregnancies();
      toast.success('Aborto registrado');
    } catch (error) {
      console.error('Error al registrar aborto:', error);
      toast.error('Error al registrar aborto');
      throw error;
    }
  };

  const getPregnanciesByCattle = (cattleId) => {
    return pregnancies.filter(p => p.cattleId === cattleId)
      .sort((a, b) => new Date(b.conceptionDate) - new Date(a.conceptionDate));
  };

  const getActivePregnancy = (cattleId) => {
    return pregnancies.find(p => p.cattleId === cattleId && p.status === 'active');
  };

  const getPregnanciesAsFather = (cattleId) => {
    return pregnancies.filter(p => p.fatherId === cattleId)
      .sort((a, b) => new Date(b.conceptionDate) - new Date(a.conceptionDate));
  };

  const value = {
    pregnancies,
    loading,
    fetchPregnancies,
    addPregnancy,
    addCheckup,
    addPalpation,
    completePregnancy,
    abortPregnancy,
    getPregnanciesByCattle,
    getActivePregnancy,
    getPregnanciesAsFather
  };

  return (
    <ReproductionContext.Provider value={value}>
      {children}
    </ReproductionContext.Provider>
  );
};
